import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, List, ListItem, ListItemText, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { CommonDataGrid } from 'features/shared/components/table';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSSelect } from '../../../shared/components/form-controls';
import { useForm, FormProvider } from 'react-hook-form';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { getAddressHistory } from '../../services/addressService';
import {
  AGENCY_FILTER_DEFAULT_VALUES,
  AGENCY_FILTER_UNASSIGNED_CODE,
  HISTORY_GRID_COLUMNS,
  DETAIL_FIELDS,
  DURATION_OPTIONS,
} from '../../constants/addressHistoryFormConstants';
import { withinDuration } from '../../utility/addressHistoryHelper';
import FormSpinner from 'components/FormSpinner';

const AddressHistoryTab = ({ ptsAddressID }) => {
  const dispatch = useDispatch();
  const { agencyOptions } = useUserDictionaryOptions();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [detailTarget, setDetailTarget] = useState(null);
  const methods = useForm({ mode: 'onChange', defaultValues: AGENCY_FILTER_DEFAULT_VALUES });
  const agencyFilter = methods.watch('AgencyFilter');
  const duration = methods.watch('Duration');

  const loadHistory = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAddressHistory(ptsAddressID),
      onSuccess: (rows) => {
        const processed = (rows || []).map((row) => ({
          ...row,
          id: row.ptsEventID,
          CaseID: (row.CaseIDs || []).filter(Boolean).join(', '),
          AgencyID: [...new Set((row.AgencyIDs || []).filter(Boolean))].join(', '),
        }));
        processed.sort((a, b) => new Date(b.Received) - new Date(a.Received));
        setEvents(processed);
      },
    });
  }, [dispatch, ptsAddressID]);

  const agencyFilterOptions = useMemo(
    () => [
      { Code: null, Description: 'All' },
      { Code: AGENCY_FILTER_UNASSIGNED_CODE, Description: 'Unassigned' },
      ...agencyOptions.map((agency) => ({ Code: agency.AgencyID, Description: agency.AgencyID })),
    ],
    [agencyOptions]
  );

  const agencyFilteredEvents = useMemo(() => {
    if (!agencyFilter) return events;
    if (agencyFilter === AGENCY_FILTER_UNASSIGNED_CODE) {
      return events.filter((row) => (row.AgencyIDs || []).filter(Boolean).length === 0);
    }
    return events.filter((row) => (row.AgencyIDs || []).includes(agencyFilter));
  }, [events, agencyFilter]);

  const durationCounts = useMemo(
    () =>
      DURATION_OPTIONS.map((option) => ({
        ...option,
        count: agencyFilteredEvents.filter((row) => withinDuration(row.Received, option.Code))
          .length,
      })),
    [agencyFilteredEvents]
  );

  const filteredEvents = useMemo(
    () => agencyFilteredEvents.filter((row) => withinDuration(row.Received, duration)),
    [agencyFilteredEvents, duration]
  );

  const handleViewDetails = useCallback((row) => setDetailTarget(row), []);
  const handleCloseDetails = useCallback(() => setDetailTarget(null), []);
  const handleDurationSelect = useCallback((code) => methods.setValue('Duration', code), [methods]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return (
    <Box className="address-history-tab">
      {loading && <FormSpinner />}
      <Box className="rms-dialog-section address-history-tab__filters">
        <FormProvider {...methods}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={4}>
              <RMSSelect
                name="AgencyFilter"
                label="Agency"
                options={agencyFilterOptions}
                optionValue="Code"
                optionLabel="Description"
                showLabelOnly
                displayEmpty
              />
            </Grid>
            <Grid item xs={4}>
              <RMSSelect
                name="Duration"
                label="Duration"
                options={DURATION_OPTIONS}
                optionValue="Code"
                optionLabel="Description"
                showLabelOnly
              />
            </Grid>
          </Grid>
        </FormProvider>

        <List dense>
          {durationCounts.map((option) => (
            <ListItem
              key={option.Code}
              button
              selected={duration === option.Code}
              onClick={() => handleDurationSelect(option.Code)}>
              <ListItemText primary={`${option.Description} - ${option.count}`} />
            </ListItem>
          ))}
        </List>
      </Box>

      <CommonDataGrid
        rows={filteredEvents}
        columns={HISTORY_GRID_COLUMNS}
        getRowId={(row) => row.ptsEventID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Address History' })}
        actions={{
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {detailTarget && (
        <RMSCommonDialog
          open={Boolean(detailTarget)}
          title={`${detailTarget.EventID || ''} - ${detailTarget.CallType || ''}`}
          onClose={handleCloseDetails}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}>
          <Grid container spacing={1}>
            {DETAIL_FIELDS.map(({ field, label, render }) => (
              <Grid item xs={6} key={field}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {label}
                </Typography>
                <Typography variant="body2">
                  {render ? render(detailTarget[field]) : detailTarget[field] || ''}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </RMSCommonDialog>
      )}
    </Box>
  );
};

export default AddressHistoryTab;
